import { useCallback, useEffect, useMemo, useState } from 'react';

import useOutsideClick from '@cobalt/react-outside-click-hook';

import TransIcon from './TransIcon';
import { ArrowSvg } from '../wf-svg';

import './index.scss';

type OptionType = {
  label: string;
  value: string;
  parentId: string;
  children: OptionType[];
};

export interface WfCascaderProps {
  value: string[];
  options: OptionType[];
  variation?: 'error' | 'warning';
  placeholder?: string;
  allowClear?: boolean;
  t: (key: string) => string;
  onChange: (value: string[]) => void;
  onClear?: () => void;
}

function WfCascader(props: WfCascaderProps) {
  const { value: selectedValues, options, variation, placeholder, allowClear, t, onChange, onClear } = props;

  const [open, setOpen] = useState(false);
  const [curLevel, setCurLevel] = useState(1);
  const [tempSelectedValues, setTempSelectedValues] = useState<string[]>([]);

  const selectInputClassName = useMemo(() => {
    const isError = variation === 'error';
    return `wf-cascader-input ${isError ? 'wf-cascader-input_error' : ''}`;
  }, [variation]);

  const genOptionMap = useCallback((options: OptionType[]): Record<string, OptionType> => {
    return options.reduce<Record<string, OptionType>>((prev, cur) => {
      const { value, children } = cur;
      prev[value] = cur;
      return Object.assign(prev, genOptionMap(children));
    }, {});
  }, []);

  const optionMap = useMemo(() => {
    return genOptionMap(options);
  }, [genOptionMap, options]);

  const label = useMemo(() => {
    return selectedValues.map((value) => optionMap[value].label).join('/');
  }, [optionMap, selectedValues]);

  const outsideClickHandler = useCallback(() => setOpen(false), []);
  const { onClickCapture } = useOutsideClick(outsideClickHandler);

  const handleChange = useCallback(
    (value: string, level: number, isFinal?: boolean) => {
      if (value === '') {
        const level = curLevel - 1;
        setCurLevel(level);
        setTempSelectedValues((tempSelectedValues) => tempSelectedValues.slice(0, level - 1));
        return;
      }

      const result = tempSelectedValues.slice(0, level);
      result[level - 1] = value;

      if (!isFinal) {
        setCurLevel((curLevel) => curLevel + 1);
        setTempSelectedValues(result);
      } else {
        onChange(result);
        setOpen(false);
      }
    },
    [curLevel, onChange, tempSelectedValues],
  );

  const handleClear = useCallback(() => {
    setCurLevel(1);
    onClear?.();
    onChange([]);
    setOpen(false);
  }, [onChange, onClear]);

  useEffect(() => {
    setCurLevel(selectedValues.length || 1);
    setTempSelectedValues(selectedValues);
  }, [open, selectedValues]);

  return (
    <div className="wf-cascader-container" onClick={onClickCapture}>
      <div className={selectInputClassName} onClick={() => setOpen((open) => !open)}>
        {label ? (
          <span className="wf-cascader-input-label" title={label}>
            {label}
          </span>
        ) : (
          <span className="wf-cascader-input-placeholder">{placeholder}</span>
        )}

        <TransIcon open={open} allowClear={allowClear} hasSelected={!!label} onClear={handleClear} />
      </div>

      {open && (
        <>
          <div className="wf-cascader-popup-container">
            <div className="wf-cascader-popup-wrap">
              <div className="wf-cascader-list-wrap" style={{ left: `-${(curLevel - 1) * 100}%` }}>
                {/* one */}
                <ul className="wf-cascader-list">
                  {options.map(({ label, value, children }) => (
                    <li
                      key={value}
                      className={`wf-cascader-option ${
                        tempSelectedValues.includes(value) ? 'wf-cascader-option_selected' : ''
                      }`}
                      title={label}
                      onClick={() => handleChange(value, 1, children.length === 0)}
                    >
                      {label}
                    </li>
                  ))}
                </ul>

                {/* two */}
                <ul className="wf-cascader-list">
                  <li className="wf-cascader-back" onClick={() => handleChange('', 2)}>
                    <ArrowSvg style={{ transform: 'rotate(90deg)' }} />
                    {t('webform.back')}
                  </li>

                  {optionMap[tempSelectedValues[0]]?.children.map(({ label, value, children }) => (
                    <li
                      key={value}
                      className={`wf-cascader-option ${
                        tempSelectedValues.includes(value) ? 'wf-cascader-option_selected' : ''
                      }`}
                      title={label}
                      onClick={() => handleChange(value, 2, children.length === 0)}
                    >
                      {label}
                    </li>
                  ))}
                </ul>

                {/* three */}
                <ul className="wf-cascader-list">
                  <li className="wf-cascader-back" onClick={() => handleChange('', 3)}>
                    <ArrowSvg style={{ transform: 'rotate(90deg)' }} />
                    {t('webform.back')}
                  </li>

                  {optionMap[tempSelectedValues[1]]?.children.map(({ label, value, children }) => (
                    <li
                      key={value}
                      className={`wf-cascader-option ${
                        tempSelectedValues.includes(value) ? 'wf-cascader-option_selected' : ''
                      }`}
                      title={label}
                      onClick={() => handleChange(value, 3, children.length === 0)}
                    >
                      {label}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default WfCascader;
